import { MakeModels } from 'common/hooks/useModels';
import { MileageRange } from 'common/types/mileage-range';
import { SearchParams } from 'common/types/SearchParams';
import { sortParams } from 'common/types/sort-params';
import { FilterOperator, FilterParam } from 'graphql/generated';
import { ZipCodeObject } from 'redux/search-param.slice';

export const useListingsSearch = () => {
  const buildFilter = (params: SearchParams) => {
    // console.log('params', params);
    const filters: FilterParam[] = [];

    const buildBodyTypeParam = (): FilterParam => {
      if (params.bodys?.length === 1) return { field: 'bodyType', strVal: params.bodys[0] };
      else return { operator: FilterOperator.in, field: 'bodyType', strVals: params.bodys };
    };
    if (params.bodys?.length) filters.push(buildBodyTypeParam());

    const buildTitleStatusParam = (): FilterParam => {
      if (params.titleSs?.length === 1) return { field: 'titleStatus', strVal: params.titleSs[0] };
      else return { operator: FilterOperator.in, field: 'titleStatus', strVals: params.titleSs };
    };
    if (params.titleSs?.length) filters.push(buildTitleStatusParam());

    const buildColorTypeParam = (): FilterParam => {
      if (params.colors?.length === 1) return { field: 'colorType', strVal: params.colors[0] };
      else return { operator: FilterOperator.in, field: 'colorType', strVals: params.colors };
    };
    if (params.colors?.length) filters.push(buildColorTypeParam());

    const buildConditionTypeParam = (): FilterParam => {
      if (params.conditions?.length === 1) return { field: 'conditionType', strVal: params.conditions[0] };
      else return { operator: FilterOperator.in, field: 'conditionType', strVals: params.conditions };
    };
    if (params.conditions?.length) filters.push(buildConditionTypeParam());

    const buildCylinderTypeParam = (): FilterParam => {
      if (params.cylinders?.length === 1) return { field: 'cylinderType', strVal: params.cylinders[0] };
      else return { operator: FilterOperator.in, field: 'cylinderType', strVals: params.cylinders };
    };
    if (params.cylinders?.length) filters.push(buildCylinderTypeParam());

    const buildDoorTypeParam = (): FilterParam => {
      if (params.doors?.length === 1) return { field: 'doorType', strVal: params.doors[0] };
      else return { operator: FilterOperator.in, field: 'doorType', strVals: params.doors };
    };
    if (params.doors?.length) filters.push(buildDoorTypeParam());

    const buildDrivetrainTypeParam = (): FilterParam => {
      if (params.drivetrains?.length === 1) return { field: 'drivetrainType', strVal: params.drivetrains[0] };
      else return { operator: FilterOperator.in, field: 'drivetrainType', strVals: params.drivetrains };
    };
    if (params.drivetrains?.length) filters.push(buildDrivetrainTypeParam());

    const buildFuelTypeParam = (): FilterParam => {
      if (params.fuels?.length === 1) return { field: 'fuelType', strVal: params.fuels[0] };
      else return { operator: FilterOperator.in, field: 'fuelType', strVals: params.fuels };
    };
    if (params.fuels?.length) filters.push(buildFuelTypeParam());

    const buildMakeParam = (makeModel: MakeModels): FilterParam => {
      if (makeModel.models?.length === 0) return { field: 'make', strVal: makeModel.make };
      else if (makeModel.models?.length === 1)
        return {
          operator: FilterOperator.and,
          filters: [
            { field: 'make', strVal: makeModel.make },
            { field: 'model', strVal: makeModel.models[0] },
          ],
        };
      else
        return {
          operator: FilterOperator.and,
          filters: [
            { field: 'make', strVal: makeModel.make },
            { operator: FilterOperator.in, field: 'model', strVals: makeModel.models },
          ],
        };
    };

    const buildMakeParams = (makeModels: MakeModels[]): FilterParam => {
      if (makeModels.length === 1) {
        return buildMakeParam(makeModels[0]);
      } else {
        const orFilters: FilterParam[] = [];
        for (const makeModel of makeModels) {
          orFilters.push(buildMakeParam(makeModel));
        }
        return { operator: FilterOperator.or, filters: orFilters };
      }
    };
    if (params.makeModels?.length) filters.push(buildMakeParams(params.makeModels));

    const buildTransmissionTypeParam = (): FilterParam => {
      if (params.transmissions?.length === 1) return { field: 'transmissionType', strVal: params.transmissions[0] };
      else
        return {
          operator: FilterOperator.in,
          field: 'transmissionType',
          strVals: params.transmissions,
        };
    };
    if (params.transmissions?.length) filters.push(buildTransmissionTypeParam());

    const buildYearRangeParam = (valMin: string, valMax: string): FilterParam => ({
      operator: FilterOperator.between,
      field: 'year',
      intVals: [+valMin, +valMax],
    });

    const buildYearMinParam = (val: string): FilterParam => ({
      operator: FilterOperator.gte,
      field: 'year',
      intVal: +val,
    });

    const buildYearMaxParam = (val: string): FilterParam => ({
      operator: FilterOperator.lte,
      field: 'year',
      intVal: +val,
    });

    if (params.yearMin?.length && params.yearMax?.length)
      filters.push(buildYearRangeParam(params.yearMin, params.yearMax));
    else {
      if (params.yearMin?.length) filters.push(buildYearMinParam(params.yearMin));
      if (params.yearMax?.length) filters.push(buildYearMaxParam(params.yearMax));
    }

    const buildPriceRangeParam = (valMin: string, valMax: string): FilterParam => ({
      operator: FilterOperator.between,
      field: 'price',
      intVals: [+valMin, +valMax],
    });

    const buildPriceMinParam = (val: string): FilterParam => ({
      operator: FilterOperator.gte,
      field: 'price',
      intVal: +val,
    });

    const buildPriceMaxParam = (val: string): FilterParam => ({
      operator: FilterOperator.lte,
      field: 'price',
      intVal: +val,
    });

    if (params.priceMin?.length && params.priceMax?.length)
      filters.push(buildPriceRangeParam(params.priceMin, params.priceMax));
    else {
      if (params.priceMin?.length) filters.push(buildPriceMinParam(params.priceMin));
      if (params.priceMax?.length) filters.push(buildPriceMaxParam(params.priceMax));
    }

    const buildMileageRangeParam = (): FilterParam => {
      let intVal: number;
      switch (params.mileage as MileageRange) {
        case MileageRange.under25:
          intVal = 25000;
          break;
        case MileageRange.under50:
          intVal = 50000;
          break;
        case MileageRange.under75:
          intVal = 75000;
          break;
        case MileageRange.under100:
          intVal = 100000;
          break;
        case MileageRange.under125:
          intVal = 125000;
          break;
        case MileageRange.under150:
          intVal = 150000;
          break;
        case MileageRange.under175:
          intVal = 175000;
          break;
        case MileageRange.under200:
          intVal = 200000;
          break;
      }
      return {
        operator: FilterOperator.lt,
        field: 'mileage',
        intVal,
      };
    };
    if (params.mileage?.length) filters.push(buildMileageRangeParam());

    const buildGeoParam = (distance: string, zo: ZipCodeObject): FilterParam => ({
      operator: FilterOperator.near,
      field: 'geoLocation',
      geoParam: {
        distance: +distance,
        longitude: zo ? zo.longitude : 0,
        latitude: zo ? zo.latitude : 0,
      },
    });
    // console.log(params);
    if (params.distance && params.distance !== '*' && params.zipCodeObj)
      filters.push(buildGeoParam(params.distance, params.zipCodeObj));
    return {
      filters,
      sortKeys: [[sortParams[+params.sortIdx].field, sortParams[+params.sortIdx].direction]],
    };
  };

  // const search = async (params: SearchParams2) => {
  //   const queryParams = buildFilter(params);
  //   console.log({ params, queryParams });
  //   const { data } = await client.query<ListingsSearchQuery>({
  //     query: LISTINGS_SEARCH_QUERY,
  //     variables: {
  //       filters: queryParams.filters,
  //       skip: params.page * params.rowsPP,
  //       limit: params.rowsPP,
  //       sortKeys: queryParams.sortKeys,
  //     },
  //   });
  //   return data.listingsSearch;
  // };

  return {
    // search,
    buildFilter,
  };
};
